import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { patientApi, orgApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Search, Plus, User, ChevronRight, AlertTriangle, Loader2, Upload, Download,
  UserCheck, Share2, UserPlus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function PatientList() {
  const { user: currentUser } = useAuth();
  const isAdmin = ['super_admin', 'practice_admin'].includes(currentUser?.role);
  const canAssignNurses = ['super_admin', 'practice_admin', 'care_coordinator'].includes(currentUser?.role);
  const isNurse = currentUser?.role === 'nurse';
  const supportsMyPatientsToggle = isNurse || canAssignNurses;
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [view, setView] = useState(isNurse ? 'all' : 'all'); // 'all' | 'mine'
  const [myPatientIds, setMyPatientIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null); // { patient, mode: 'assign'|'share' }
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Super-admin only: org targeting for bulk upload
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [targetOrgId, setTargetOrgId] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [form, setForm] = useState({
    first_name: '', last_name: '', date_of_birth: '', gender: 'male',
    email: '', phone: '', insurance_provider: '', address: '',
    primary_language: '', medical_practitioner: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    allergies: '', medical_history: '', chief_concern: '',
    pharmacy_name: '', pharmacy_phone: '',
    primary_caregiver_name: '', primary_caregiver_relationship: '', primary_caregiver_phone: ''
  });

  useEffect(() => { loadPatients(); loadMyPatientIds(); }, []);

  useEffect(() => {
    const debounce = setTimeout(() => { loadPatients(); }, 300);
    return () => clearTimeout(debounce);
  }, [search, view]);

  const loadMyPatientIds = async () => {
    if (!supportsMyPatientsToggle) return;
    try {
      const r = await axios.get(`${API_URL}/api/patient-assignments/my-patient-ids`, { headers: authHeader() });
      setMyPatientIds(new Set(r.data.patient_ids || []));
    } catch (err) { /* silent */ }
  };

  const loadPatients = async () => {
    setLoading(true);
    try {
      const params = { search, limit: 100 };
      if (view === 'mine') params.assigned_to_me = true;
      const response = await patientApi.list(params);
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to load patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.first_name || !form.last_name || !form.date_of_birth || !form.gender) {
      toast.error('First name, last name, DOB and gender are required');
      return;
    }
    setSaving(true);
    try {
      await patientApi.create(form);
      toast.success('Patient created successfully');
      setShowCreate(false);
      setForm({ first_name: '', last_name: '', date_of_birth: '', gender: 'male', email: '', phone: '', insurance_provider: '' });
      loadPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create patient');
    } finally {
      setSaving(false);
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = ''; // reset so same file can be re-picked

    // Super admin must pick a target organization before we send the file
    if (isSuperAdmin) {
      setPendingFile(file);
      setUploadResult(null);
      try {
        const r = await orgApi.list();
        const list = Array.isArray(r.data) ? r.data : (r.data.organizations || []);
        setOrgs(list);
        // Default to first org if none chosen yet
        if (!targetOrgId && list[0]) setTargetOrgId(list[0].id);
      } catch (err) {
        toast.error('Could not load organizations');
      }
      setShowUploadDialog(true);
      return;
    }
    // Practice admin — upload immediately (always targets their own org)
    await performUpload(file, null);
  };

  const performUpload = async (file, organizationId) => {
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (organizationId) fd.append('organization_id', organizationId);
      const res = await patientApi.uploadExcel(fd);
      const { created, errors = [], total_rows = 0, organization_name } = res.data;
      toast.success(`Imported ${created} patient${created === 1 ? '' : 's'}${organization_name ? ` into ${organization_name}` : ''}`);
      if (errors.length > 0) {
        toast.error(`${errors.length} row${errors.length === 1 ? '' : 's'} had errors — see details below`);
      }
      setUploadResult({ created, errors, total_rows, organization_name });
      loadPatients();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
      setUploadResult({ created: 0, errors: [typeof msg === 'string' ? msg : 'Unknown error'], total_rows: 0 });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'First Name', 'Last Name', 'DOB', 'Gender', 'Email', 'Phone', 'Address',
      'Insurance Provider', 'Insurance ID', 'Primary Language',
      'Emergency Contact Name', 'Emergency Contact Phone', 'Emergency Contact Relationship',
      'Allergies', 'Medical History', 'Chief Concern', 'Pharmacy Name', 'Pharmacy Phone',
    ];
    const samples = [
      ['Amelia', 'Thompson', '1987-03-14', 'Female', 'amelia.t@example.com', '(555) 234-5678', '123 Main St, Springfield, IL 62701', 'Blue Cross', 'INS123456', 'English', 'John Thompson', '(555) 234-5679', 'Spouse', 'Penicillin', 'Type 2 Diabetes; Hypertension', 'Annual physical', 'CVS Pharmacy', '(555) 555-7676'],
      ['Jackson', 'Bennett', '03/22/1954', 'Male', 'jackson.b@example.com', '(555) 345-6789', '456 Oak Ave, Springfield, IL 62702', 'Medicare', 'INS789012', 'English', 'Mary Bennett', '(555) 345-6790', 'Spouse', 'None', 'Coronary Artery Disease', 'Chest pain follow-up', 'Walgreens', '(555) 555-3232'],
      ['Olivia', 'Carter', '1992-11-30', 'Female', '', '', '', '', '', 'Spanish', '', '', '', 'Sulfa drugs', 'Asthma', '', '', ''],
    ];
    const rows = [headers, ...samples];
    // CSV-escape: wrap in quotes if contains comma, quote, or newline; double embedded quotes
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = rows.map(r => r.map(escape).join(',')).join('\n') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'patient_import_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded — fill it in and upload');
  };

  const handleConfirmUpload = async () => {
    if (!pendingFile) return;
    if (isSuperAdmin && !targetOrgId) {
      toast.error('Please select a target organization');
      return;
    }
    await performUpload(pendingFile, targetOrgId);
    setPendingFile(null);
  };

  const getRiskBadge = (score) => {
    if (score >= 70) return <span className="risk-badge-high">{score}</span>;
    if (score >= 40) return <span className="risk-badge-medium">{score}</span>;
    return <span className="risk-badge-low">{score}</span>;
  };

  const handleExportCsv = async () => {
    try {
      const res = await patientApi.exportCsv(search);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `patients_export_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${patients.length} patient(s) to CSV`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="patient-list">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {view === 'mine' ? 'My Patients' : 'Patients'}
          </h1>
          <p className="text-[#64748B] mt-1">{patients.length} patients{view === 'mine' ? ' assigned to you' : ' total'}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex items-center gap-2" onClick={handleExportCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4" />Export CSV
          </Button>
          <Button variant="outline" className="flex items-center gap-2" onClick={handleDownloadTemplate} data-testid="download-template-btn" title="Download a sample CSV template with supported columns">
            <Download className="w-4 h-4" />Template
          </Button>
          <label className="cursor-pointer">
            <Button variant="outline" className="flex items-center gap-2" asChild={false} onClick={() => document.getElementById('excel-upload').click()} disabled={uploading} data-testid="upload-excel-btn">
              <Upload className="w-4 h-4" />{uploading ? 'Uploading...' : 'Import Excel'}
            </Button>
            <input id="excel-upload" type="file" className="hidden" accept=".xlsx,.xls,.csv" onChange={handleExcelUpload} />
          </label>
          <Button className="careiq-btn-primary flex items-center gap-2" data-testid="add-patient-btn" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4" />
            Add Patient
          </Button>
        </div>
      </div>

      <Card className="careiq-card">
        <CardContent className="p-4">
          {supportsMyPatientsToggle && (
            <div className="flex items-center gap-2 mb-3" data-testid="patient-view-toggle">
              <button
                onClick={() => setView('mine')}
                data-testid="view-mine-chip"
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  view === 'mine' ? 'bg-[#0E7490] text-white border-[#0E7490]' :
                  'bg-white text-[#0A2342] border-[#E2E8F0] hover:bg-[#F8FAFC]'
                }`}
              >
                <UserCheck className="w-3.5 h-3.5 inline mr-1.5" />
                Assigned to me{myPatientIds.size ? ` · ${myPatientIds.size}` : ''}
              </button>
              <button
                onClick={() => setView('all')}
                data-testid="view-all-chip"
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  view === 'all' ? 'bg-[#0E7490] text-white border-[#0E7490]' :
                  'bg-white text-[#0A2342] border-[#E2E8F0] hover:bg-[#F8FAFC]'
                }`}
              >
                All practice patients
              </button>
            </div>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
            <Input placeholder="Search by name or MRN..." value={search} onChange={(e) => setSearch(e.target.value)} className="careiq-input pl-10" data-testid="patient-search-input" />
          </div>
        </CardContent>
      </Card>

      <Card className="careiq-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : patients.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="table-header text-left">Patient</th>
                    <th className="table-header text-left">MRN</th>
                    <th className="table-header text-left">DOB</th>
                    <th className="table-header text-left">Insurance</th>
                    <th className="table-header text-left">Risk Score</th>
                    <th className="table-header text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((patient) => (
                    <tr key={patient.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#0E7490]/10 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-[#0E7490]" />
                          </div>
                          <div>
                            <Link to={`/patients/${patient.id}`} className="font-medium text-[#0A2342] hover:text-[#0E7490]">
                              {patient.first_name} {patient.last_name}
                            </Link>
                            <div className="text-xs text-[#64748B]">{patient.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell font-mono text-sm">{patient.mrn}</td>
                      <td className="table-cell">{patient.date_of_birth}</td>
                      <td className="table-cell">{patient.insurance_provider || '-'}</td>
                      <td className="table-cell">{getRiskBadge(patient.risk_score)}</td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1 justify-end">
                          {canAssignNurses && (
                            <Button
                              size="sm" variant="ghost"
                              className="text-[#0E7490] h-7 px-2"
                              title="Assign nurse"
                              onClick={() => setAssignDialog({ patient, mode: 'assign' })}
                              data-testid={`assign-nurse-${patient.id}`}
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          )}
                          {isNurse && myPatientIds.has(patient.id) && (
                            <Button
                              size="sm" variant="ghost"
                              className="text-[#0E7490] h-7 px-2"
                              title="Share with another nurse"
                              onClick={() => setAssignDialog({ patient, mode: 'share' })}
                              data-testid={`share-nurse-${patient.id}`}
                            >
                              <Share2 className="w-4 h-4" />
                            </Button>
                          )}
                          <Link to={`/patients/${patient.id}`}>
                            <Button size="sm" variant="ghost" className="text-[#0E7490]"><ChevronRight className="w-4 h-4" /></Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-[#64748B]">
              <User className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
              <p>No patients found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Patient Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className={`${isAdmin ? 'sm:max-w-2xl' : 'sm:max-w-lg'}`} data-testid="create-patient-dialog">
          <DialogHeader><DialogTitle>Add New Patient</DialogTitle></DialogHeader>
          <ScrollArea className={isAdmin ? 'max-h-[70vh]' : ''}>
          <div className="space-y-4 mt-2 pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>First Name *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="patient-first-name" /></div>
              <div><Label>Last Name *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="patient-last-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date of Birth *</Label><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="patient-dob" /></div>
              <div>
                <Label>Gender *</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="patient-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="patient-email" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="patient-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Insurance Provider</Label><Input value={form.insurance_provider} onChange={(e) => setForm({ ...form, insurance_provider: e.target.value })} data-testid="patient-insurance" /></div>
              <div><Label>Primary Language</Label><Input value={form.primary_language} onChange={(e) => setForm({ ...form, primary_language: e.target.value })} /></div>
            </div>

            {/* Admin-only expanded fields */}
            {isAdmin && (
              <>
                <div className="border-t pt-3 mt-2"><span className="text-xs font-semibold text-[#0E7490]">Address & Provider</span></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div><Label>Medical Practitioner</Label><Input value={form.medical_practitioner} onChange={(e) => setForm({ ...form, medical_practitioner: e.target.value })} /></div>
                </div>

                <div className="border-t pt-3"><span className="text-xs font-semibold text-[#0E7490]">Emergency Contact</span></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Name</Label><Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
                  <div><Label>Relationship</Label><Input value={form.emergency_contact_relationship} onChange={(e) => setForm({ ...form, emergency_contact_relationship: e.target.value })} /></div>
                </div>

                <div className="border-t pt-3"><span className="text-xs font-semibold text-[#0E7490]">Medical Info</span></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Allergies</Label><Textarea value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} className="min-h-[50px]" /></div>
                  <div><Label>Medical History</Label><Textarea value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })} className="min-h-[50px]" /></div>
                </div>
                <div><Label>Chief Concern</Label><Input value={form.chief_concern} onChange={(e) => setForm({ ...form, chief_concern: e.target.value })} /></div>

                <div className="border-t pt-3"><span className="text-xs font-semibold text-[#0E7490]">Caregiver & Pharmacy</span></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Caregiver Name</Label><Input value={form.primary_caregiver_name} onChange={(e) => setForm({ ...form, primary_caregiver_name: e.target.value })} /></div>
                  <div><Label>Relationship</Label><Input value={form.primary_caregiver_relationship} onChange={(e) => setForm({ ...form, primary_caregiver_relationship: e.target.value })} /></div>
                  <div><Label>Caregiver Phone</Label><Input value={form.primary_caregiver_phone} onChange={(e) => setForm({ ...form, primary_caregiver_phone: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Pharmacy Name</Label><Input value={form.pharmacy_name} onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })} /></div>
                  <div><Label>Pharmacy Phone</Label><Input value={form.pharmacy_phone} onChange={(e) => setForm({ ...form, pharmacy_phone: e.target.value })} /></div>
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleCreate} disabled={saving} data-testid="save-patient-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : 'Create Patient'}
              </Button>
            </div>
          </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog — Super admin picks target org + sees result */}
      <Dialog open={showUploadDialog} onOpenChange={(v) => { if (!v) { setShowUploadDialog(false); setPendingFile(null); setUploadResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Import Patients from File</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {pendingFile && (
              <div className="p-3 rounded border border-[#E2E8F0] bg-[#F8FAFC] text-sm">
                <div className="font-medium text-[#0A2342] truncate">{pendingFile.name}</div>
                <div className="text-[10px] text-[#64748B]">{(pendingFile.size / 1024).toFixed(1)} KB · {pendingFile.type || 'file'}</div>
              </div>
            )}

            {isSuperAdmin && (
              <div>
                <Label>Target Organization <span className="text-[#991B1B]">*</span></Label>
                <Select value={targetOrgId} onValueChange={setTargetOrgId}>
                  <SelectTrigger className="mt-1" data-testid="upload-org-select"><SelectValue placeholder="Pick an organization" /></SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.display_name || o.name || o.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#64748B] mt-1">Patients will be created with this organization's ID.</p>
              </div>
            )}

            <div className="p-3 rounded border border-[#E2E8F0] bg-[#F0FDFA] text-[10px] text-[#0A2342]">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <strong>Supported columns:</strong> First Name, Last Name, DOB (YYYY-MM-DD or MM/DD/YYYY), Gender, Email, Phone, Address, Insurance Provider, Insurance ID, Primary Language, Emergency Contact Name/Phone/Relationship, Allergies, Medical History.
                </div>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="shrink-0 inline-flex items-center gap-1 text-[#0E7490] hover:text-[#0A2342] underline whitespace-nowrap"
                  data-testid="download-template-inline-btn"
                >
                  <Download className="w-3 h-3" />Get template
                </button>
              </div>
            </div>

            {uploadResult && (
              <div className={`p-3 rounded border text-sm ${uploadResult.errors.length ? 'border-[#FECACA] bg-[#FEF2F2]' : 'border-[#A7F3D0] bg-[#ECFDF5]'}`}>
                <div className="font-medium text-[#0A2342]">
                  {uploadResult.created} imported / {uploadResult.total_rows} rows{uploadResult.organization_name ? ` into ${uploadResult.organization_name}` : ''}
                </div>
                {uploadResult.detected_headers?.length > 0 && (
                  <div className="mt-1 text-[10px] text-[#64748B]">
                    <strong>Detected columns:</strong> {uploadResult.detected_headers.join(', ')}
                  </div>
                )}
                {uploadResult.unmapped_headers?.length > 0 && (
                  <div className="mt-1 text-[10px] text-[#92400E]">
                    <strong>Unmapped (ignored):</strong> {uploadResult.unmapped_headers.join(', ')}
                  </div>
                )}
                {uploadResult.errors.length > 0 && (
                  <ul className="mt-2 list-disc list-inside text-[11px] text-[#991B1B] max-h-40 overflow-y-auto space-y-0.5">
                    {uploadResult.errors.slice(0, 30).map((e, i) => <li key={i}>{e}</li>)}
                    {uploadResult.errors.length > 30 && <li>… and {uploadResult.errors.length - 30} more</li>}
                  </ul>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowUploadDialog(false); setPendingFile(null); setUploadResult(null); }} data-testid="upload-cancel-btn">
                {uploadResult ? 'Close' : 'Cancel'}
              </Button>
              {!uploadResult && (
                <Button className="careiq-btn-primary" onClick={handleConfirmUpload} disabled={uploading || !pendingFile || (isSuperAdmin && !targetOrgId)} data-testid="upload-confirm-btn">
                  {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : <><Upload className="w-4 h-4 mr-2" />Import</>}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {assignDialog && (
        <NurseAssignmentDialog
          patient={assignDialog.patient}
          mode={assignDialog.mode}
          currentUser={currentUser}
          onClose={() => setAssignDialog(null)}
          onSaved={() => {
            setAssignDialog(null);
            loadMyPatientIds();
            if (view === 'mine') loadPatients();
          }}
        />
      )}
    </div>
  );
}

// -------------------- Nurse Assignment / Share Dialog --------------------

function NurseAssignmentDialog({ patient, mode, currentUser, onClose, onSaved }) {
  const [nurses, setNurses] = React.useState([]);
  const [selectedNurseId, setSelectedNurseId] = React.useState('');
  const [accessLevel, setAccessLevel] = React.useState(mode === 'share' ? 'forms_chart' : 'primary');
  const [note, setNote] = React.useState('');
  const [existing, setExisting] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [usersRes, assignmentsRes] = await Promise.all([
          axios.get(`${API_URL}/api/patient-assignments/nurses`, { headers: authHeader() }).catch(() => ({ data: { nurses: [] } })),
          axios.get(`${API_URL}/api/patient-assignments?patient_id=${patient.id}`, { headers: authHeader() }),
        ]);
        if (cancelled) return;
        const userList = usersRes.data?.nurses || [];
        // Exclude self when sharing
        setNurses(userList.filter(u => mode !== 'share' || u.id !== currentUser?.id));
        setExisting(assignmentsRes.data.assignments || []);
      } catch (err) {
        toast.error('Failed to load nurses');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patient.id, mode, currentUser?.id]);

  const submit = async () => {
    if (!selectedNurseId) { toast.error('Pick a nurse'); return; }
    setSaving(true);
    try {
      if (mode === 'share') {
        await axios.post(`${API_URL}/api/patient-assignments/share`,
          { patient_id: patient.id, nurse_id: selectedNurseId, note }, { headers: authHeader() });
        toast.success('Patient shared');
      } else {
        await axios.post(`${API_URL}/api/patient-assignments`,
          { patient_id: patient.id, nurse_id: selectedNurseId, access_level: accessLevel, note },
          { headers: authHeader() });
        toast.success('Nurse assigned');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const revoke = async (assignmentId) => {
    if (!window.confirm('Revoke this assignment?')) return;
    try {
      await axios.delete(`${API_URL}/api/patient-assignments/${assignmentId}`, { headers: authHeader() });
      toast.success('Revoked');
      // Refresh existing list
      const r = await axios.get(`${API_URL}/api/patient-assignments?patient_id=${patient.id}`, { headers: authHeader() });
      setExisting(r.data.assignments || []);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    }
  };

  const headerLabel = mode === 'share' ? 'Share patient with another nurse' : 'Assign nurse to patient';

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="nurse-assignment-dialog">
        <DialogHeader>
          <DialogTitle>{headerLabel}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="text-xs text-[#64748B]">
            Patient: <span className="font-medium text-[#0A2342]">{patient.first_name} {patient.last_name}</span> <span className="font-mono text-[10px]">({patient.mrn})</span>
          </div>
          {loading ? (
            <div className="text-center py-4 text-xs text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : (
            <>
              {existing.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">Active assignments</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="existing-assignments">
                    {existing.map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-xs border border-[#E2E8F0] rounded px-2 py-1">
                        <div>
                          <div className="text-[#0A2342] font-medium">{a.nurse_name}</div>
                          <div className="text-[10px] text-[#64748B]">
                            {a.source === 'share' ? 'Shared' : 'Assigned'} · {a.access_level === 'primary' ? 'Full' : 'Forms + chart'}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[#991B1B] text-[10px]"
                                onClick={() => revoke(a.id)} data-testid={`revoke-${a.id}`}>Revoke</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label>Nurse *</Label>
                <select
                  value={selectedNurseId}
                  onChange={(e) => setSelectedNurseId(e.target.value)}
                  className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm"
                  data-testid="assign-nurse-select"
                >
                  <option value="">Pick a nurse…</option>
                  {nurses.map((n) => (
                    <option key={n.id} value={n.id}>{n.name || n.email}</option>
                  ))}
                </select>
                {nurses.length === 0 && (
                  <p className="text-[10px] text-[#94A3B8] mt-1">No nurses available in your organization.</p>
                )}
              </div>

              {mode !== 'share' && (
                <div>
                  <Label>Access level</Label>
                  <select
                    value={accessLevel}
                    onChange={(e) => setAccessLevel(e.target.value)}
                    className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm"
                    data-testid="assign-access-select"
                  >
                    <option value="primary">Primary — full chart access</option>
                    <option value="forms_chart">Forms + chart (no demographic / medication edits)</option>
                  </select>
                </div>
              )}

              <div>
                <Label>Note (optional)</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)}
                       placeholder="e.g. weekend coverage" data-testid="assign-note-input" />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E8F0]">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving || !selectedNurseId}
                    className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="assign-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'share' ? 'Share' : 'Assign')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}